<template>
  <o-dialog
    ref="dialog"
    :title="$t('order.addDiscount')"
    :info="$t('components.order.actions.addDiscount.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('order.addDiscount'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div>
      <o-field-group
        :label="$t('general.discount')"
        :name="$t('general.discount')"
        class="mb-4 w-full"
      >
        <div class="flex items-center">
          <discount-select v-model="discount" class="flex-grow" />

          <UButton icon="lucide:plus" class="ml-2" @click="handleCreate" />
        </div>
      </o-field-group>

      <div v-if="discount" class="mt-6 pt-4 text-left -mx-4 bg-gray-50 dark:bg-gray-900 border-t border-b px-4">
        <o-form ref="form">
          <o-field-group
            v-slot="{ status }"
            :label="$t('general.price')"
            :name="$t('general.price')"
            class="mb-6 block"
            :rules="isAmountTypePercentage ? 'required|between:0,100' : 'required|currency'"
            :disabled="isLocked"
          >
            <div class="flex items-center space-x-2">
              <currency-field v-if="isAmountTypeFixed" v-model="_price" :status="status" :disabled="isLocked" class="w-1/2" />
              <number-field
                v-if="isAmountTypePercentage"
                v-model="_price"
                :min="0"
                :max="100"
                slim
                :status="status"
                trailing="%"
                class="w-1/2"
                :disabled="isLocked"
              />
              <segment-field v-model="discount.amountType" :options="discountAmountTypeOptions" :disabled="isLocked" class="border w-1/2" />
            </div>
          </o-field-group>

          <o-field-group v-if="isAmountTypeFixed" :label="$t('general.tax')">
            <segment-field v-model="discount.discountTaxType" :options="discountTaxTypeOptions" :disabled="isLocked" class="border mb-6" />
          </o-field-group>
        </o-form>
      </div>
    </div>

    <edit-dialog
      ref="editDialog"
      @success="handleSync"
    />
  </o-dialog>
</template>

<script setup lang="ts">
import { DiscountAmountType, DiscountAmountTypeLabels, DiscountTaxType, DiscountTaxTypeLabels } from '@/_api/models/discountModel'

import DiscountSelect from '@/components/discountModel/elements/Select.vue'
import EditDialog from '@/components/discountModel/actions/Edit.vue'
import {
  NumberField,
  SegmentField,
  CurrencyField,
} from '@/components/fields'

import { useDiscountModelStore } from '~/stores/useDiscountModelStore'
import { UButton } from '#components'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification } = useNuxtApp()
const { t } = useI18n()

const discountModelStore = useDiscountModelStore()

const dialog = ref<any>()
const form = ref<any>()
const editDialog = ref<any>()

const discount = ref<any>(null)
const recents = ref<any[]>([])

const _price = computed({
  get () {
    return discount.value?.price
  },
  set (val: any) {
    discount.value.price = Number(val)
  },
})

const isLocked = computed(() => discount.value?.locked)

const isAmountTypeFixed = computed(() => discount.value?.isAmountTypeFixed)

const isAmountTypePercentage = computed(() => discount.value?.isAmountTypePercentage)

const discountAmountTypeOptions = computed(() => {
  return Object.values(DiscountAmountType)
    .filter((v): v is DiscountAmountType => typeof v === 'number')
    .map((value) => ({
      label: t(DiscountAmountTypeLabels[value]),
      value,
    }))
})

const discountTaxTypeOptions = computed(() => {
  return Object.values(DiscountTaxType)
    .filter((v): v is DiscountTaxType => typeof v === 'number')
    .map((value) => ({
      label: t(DiscountTaxTypeLabels[value]),
      value,
    }))
})

function open () {
  discount.value = null
  dialog.value.open()
}

async function confirm () {
  emit('update:loading', true)

  try {
    const discountVal = discount.value
    const response = await $api.order.addDiscount(String(props.order.idOrder), discountVal)
    const order = response.data

    emit('update:order', order)

    $notification.toast({
      title: t('components.order.actions.addDiscount.success'),
      text: t('components.order.actions.addDiscount.successInfo'),
      variant: 'success',
    })

    emit('update:loading', false)

    return true
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    console.log(e)
  }

  emit('update:loading', false)
}

function validate () {
  return discount.value && form.value?.validate()
}

function handleCreate () {
  editDialog.value.open()
}

function handleSync (discountVal: any) {
  discount.value = discountVal
  discountModelStore.sync({ force: true })
}

defineExpose({ open })
</script>
